import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSelector } from 'react-redux';
import { isNil } from 'lodash-es';
import { DateTime } from 'luxon';
import { manageEvent } from './eventHandlers/eventHandlerManager';
import { selectSelectedRestaurantId } from '../../store/auth/selectors';

export function useRestaurantSse(): void {
	const { isAuthenticated, getAccessTokenSilently } = useAuth0();
	const selectedRestaurantId = useSelector(selectSelectedRestaurantId);

	const esRef = useRef<EventSource | null>(null);
	const renewTimerRef = useRef<number | null>(null);
	const backoffRef = useRef<number>(1000);
	const cancelledRef = useRef(false);
	const fetchAbortRef = useRef<AbortController | null>(null);
	const openingRef = useRef(false); // prevent concurrent opens

	useEffect(() => {
		if (!isAuthenticated || !selectedRestaurantId) {
			return;
		}

		if (!process.env.REACT_APP_SERVER_ENDPOINT || !process.env.REACT_APP_AUTH0_AUDIENCE) {
			return;
		}

		cancelledRef.current = false;

		const clearTimers = (): void => {
			if (renewTimerRef.current) {
				window.clearTimeout(renewTimerRef.current);
				renewTimerRef.current = null;
			}
		};

		const closeStream = (): void => {
			esRef.current?.close();
			esRef.current = null;
			clearTimers();
			openingRef.current = false;
		};

		const scheduleRenew = (expiresAt: string): void => {
			const advance = 60_000; // renew ~60s early
			let msUntilRenew = 2500;

			let exp: DateTime;

			if (typeof expiresAt === 'number') {
				// JWT exp is in seconds since epoch
				exp = DateTime.fromSeconds(expiresAt).toUTC();
			} else {
				// Keep the zone from the string if present; otherwise treat as local and convert to UTC
				const parsed = DateTime.fromISO(expiresAt, { setZone: true });

				exp = parsed.isValid ? parsed.toUTC() : DateTime.invalid('Invalid expiresAt');
			}

			if (exp.isValid) {
				const now = DateTime.utc();
				const msUntilExp = exp.diff(now).as('milliseconds');
				const delay = Math.max(msUntilExp - advance, 0);

				// Clamp to a sane minimum to prevent tight loops; optionally clamp a max too
				msUntilRenew = Math.max(delay, 2500);
			}

			renewTimerRef.current = window.setTimeout(() => {
				openStream();
			}, msUntilRenew);
		};

		const openStream = async (): Promise<void> => {
			if (openingRef.current) {
				return;
			}

			openingRef.current = true;

			// abort any in-flight token request
			fetchAbortRef.current?.abort();
			const ac = new AbortController();

			fetchAbortRef.current = ac;

			try {
				closeStream(); // ensure clean start

				// 1) API access token
				let accessToken: string;

				try {
					accessToken = await getAccessTokenSilently({
						authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
					});
				} catch (err: any) {
					const code = err?.error || err?.code;

					if (code === 'login_required' || code === 'consent_required' || code === 'missing_refresh_token') {
						openingRef.current = false;

						return; // user needs to login/consent
					}

					throw err;
				}

				// 2) Ask server for stream token (POST is correct)
				const res = await fetch(`${process.env.REACT_APP_SERVER_ENDPOINT}/api/restaurant/stream-token`, {
					method: 'POST',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						'restaurant-id': selectedRestaurantId,
					},
					// drop cookies unless you truly need them for this route
					credentials: 'omit',
					signal: ac.signal,
				});

				if (!res.ok) {
					throw new Error(`stream-token ${res.status}`);
				}

				const { token, expiresAt } = await res.json();

				if (cancelledRef.current) {
					openingRef.current = false;

					return;
				}

				// 3) Open SSE
				const url = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/restaurant/stream/${encodeURIComponent(
					selectedRestaurantId
				)}?token=${encodeURIComponent(token)}`;

				const es = new EventSource(url); // EventSource never sends auth headers; token in query is fine

				esRef.current = es;

				es.onopen = (): void => {
					backoffRef.current = 1000; // reset backoff
					openingRef.current = false;
				};

				es.onmessage = (evt): void => {
					try {
						const parsed = JSON.parse(evt.data);

						manageEvent(parsed);
					} catch {
						/* ignore */
					}
				};

				es.onerror = (): void => {
					// CORS/401/closed connection leads here
					closeStream();

					if (!cancelledRef.current) {
						const delay = backoffRef.current;

						backoffRef.current = Math.min(backoffRef.current * 2, 5000);
						window.setTimeout(() => openStream(), delay);
					}
				};

				// 4) Proactive renew
				if (!isNil(expiresAt)) {
					scheduleRenew(expiresAt);
				}
			} catch {
				closeStream();

				if (!cancelledRef.current) {
					const delay = backoffRef.current;

					backoffRef.current = Math.min(backoffRef.current * 2, 5000);
					window.setTimeout(() => openStream(), delay);
				}
			}
		};

		openStream();

		const onVisible = (): void => {
			if (document.visibilityState === 'visible' && !esRef.current) {
				backoffRef.current = 1000;
				openStream();
			}
		};

		document.addEventListener('visibilitychange', onVisible);

		return () => {
			cancelledRef.current = true;
			document.removeEventListener('visibilitychange', onVisible);
			fetchAbortRef.current?.abort();
			closeStream();
		};
		// 🔑 do NOT depend on `onEvent`; we read it via ref
	}, [isAuthenticated, selectedRestaurantId, getAccessTokenSilently]);
}
